<template>
  <div>

    <!-- ── Loading skeletons ─────────────────────────────── -->
    <template v-if="loading">
      <div v-for="i in 2" :key="i"
           class="h-14 mb-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] animate-pulse" />
    </template>

    <template v-else>

      <!-- ── Empty: no active questions exist at all ─────── -->
      <div v-if="activeQuestions.length === 0"
           class="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 px-4 py-5 flex items-center gap-3">
        <div class="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
          <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-700 dark:text-gray-300">No active questions</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Create questions first, then assign them here.
            <router-link to="/questions" data-turbo-prefetch="false" class="text-blue-600 dark:text-blue-400 font-medium hover:underline">Go to Questions</router-link>
          </p>
        </div>
      </div>

      <template v-else>

        <!-- ══ No questions assigned yet — improved empty state ══ -->
        <template v-if="items.length === 0">
          <div class="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] px-6 py-10 flex flex-col items-center text-center">
            <div class="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z"/>
              </svg>
            </div>
            <h3 class="text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">No questions added yet</h3>
            <p class="text-xs text-gray-400 dark:text-gray-500 max-w-[220px] mb-5 leading-relaxed">
              Add questions to collect information from clients when they book. Things like company name, project details, or service type.
            </p>
            <button type="button" @click="openPanel"
                    class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-colors shadow-sm shadow-blue-600/20">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add first question
            </button>
          </div>
        </template>

        <!-- ══ Questions exist ══ -->
        <template v-else>

        <!-- ── Header: tab toggle + conditional logic toggle ── -->
        <div class="flex items-center gap-2 mb-3">

          <!-- Tab toggle -->
          <div class="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl flex-1 min-w-0">
            <button type="button"
                    @click="activeTab = 'questions'"
                    class="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                    :class="activeTab === 'questions'
                      ? 'bg-white dark:bg-[#1C2128] text-gray-800 dark:text-gray-200 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"/>
              </svg>
              Questions
              <span class="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-bold tabular-nums">
                {{ items.length }}
              </span>
            </button>
            <button v-if="conditionalLogicEnabled"
                    type="button"
                    @click="activeTab = 'logic'"
                    class="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                    :class="activeTab === 'logic'
                      ? 'bg-white dark:bg-[#1C2128] text-gray-800 dark:text-gray-200 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 12m0 0 4.5-9M3 12h13.5m0 0 4.5-9m-4.5 9 4.5 9"/>
              </svg>
              Logic
              <span v-if="totalConditionsCount > 0"
                    class="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 font-bold tabular-nums">
                {{ totalConditionsCount }}
              </span>
            </button>
          </div>

          <!-- Conditional logic toggle button (icon + switch only — label removed to fit docs link) -->
          <button type="button"
                  @click="toggleConditionalLogic"
                  class="shrink-0 flex items-center gap-1.5 px-2 py-1.5 rounded-xl border transition-all"
                  :class="conditionalLogicEnabled
                    ? 'border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-gray-400 dark:text-gray-500 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-600 dark:hover:text-gray-400'"
                  title="Toggle conditional logic">
            <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 12m0 0 4.5-9M3 12h13.5m0 0 4.5-9m-4.5 9 4.5 9"/>
            </svg>
            <div class="relative w-7 h-4 rounded-full transition-colors flex-shrink-0"
                 :class="conditionalLogicEnabled ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'">
              <div class="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform"
                   :class="conditionalLogicEnabled ? 'translate-x-3.5' : 'translate-x-0.5'" />
            </div>
          </button>

          <!-- Docs link -->
          <a href="/help-center/bookings/conditional-logic"
             target="_blank"
             rel="noopener"
             class="shrink-0 w-6 h-6 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-violet-400 dark:hover:border-violet-600 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-all"
             title="Conditional logic docs">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"/>
            </svg>
          </a>

        </div>

        <!-- Disable confirmation banner -->
        <Transition name="logic-expand">
          <div v-if="showDisableConfirm"
               class="mb-3 flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
            <svg class="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
            </svg>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-semibold text-amber-800 dark:text-amber-300">This will clear all configured conditions</p>
              <p class="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-0.5">
                {{ totalConditionsCount }} condition{{ totalConditionsCount === 1 ? '' : 's' }} will be removed. This cannot be undone.
              </p>
              <div class="flex items-center gap-2 mt-2">
                <button type="button" @click="disableConditionalLogic"
                        class="px-3 py-1 text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors">
                  Disable and clear
                </button>
                <button type="button" @click="showDisableConfirm = false"
                        class="px-3 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </Transition>

        <!-- ── Logic canvas tab ───────────────────────────── -->
        <div v-if="conditionalLogicEnabled && activeTab === 'logic'" class="h-[480px]">
          <LogicCanvas :items="items"
                       :event-name="eventName"
                       @edit-question-logic="onCanvasEditRequest" />
        </div>

        <!-- ── Question cards ─────────────────────────────── -->
        <div v-if="activeTab === 'questions' || !conditionalLogicEnabled">
        <div v-if="items.length > 0" class="space-y-2">
          <div v-for="(item, idx) in items" :key="item._key"
               class="rounded-xl border bg-white dark:bg-[#1C2128] transition-colors overflow-hidden"
               :class="dragOverIdx === idx && dragOverIdx !== dragIdx
                 ? 'border-blue-400 dark:border-blue-500 ring-1 ring-blue-400/30'
                 : conditionalLogicEnabled && hasStaleConditions(item, idx)
                   ? 'border-amber-300 dark:border-amber-600'
                   : conditionalLogicEnabled && item._logicOpen
                     ? 'border-violet-300 dark:border-violet-700'
                     : 'border-gray-200 dark:border-gray-700'"
               :draggable="!item._logicOpen"
               @dragstart="onDragStart(idx)"
               @dragover.prevent="onDragOver(idx)"
               @drop.prevent="onDrop(idx)"
               @dragend="onDragEnd">

            <!-- ── Card top row ── -->
            <div class="group flex items-center gap-2 pl-2.5 pr-3 py-3 select-none">

              <!-- Drag handle -->
              <div class="cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-600 hover:text-gray-400 shrink-0 px-0.5">
                <svg class="w-3.5 h-5" fill="currentColor" viewBox="0 0 14 20">
                  <circle cx="4" cy="4"  r="1.5"/><circle cx="10" cy="4"  r="1.5"/>
                  <circle cx="4" cy="10" r="1.5"/><circle cx="10" cy="10" r="1.5"/>
                  <circle cx="4" cy="16" r="1.5"/><circle cx="10" cy="16" r="1.5"/>
                </svg>
              </div>

              <!-- Position number -->
              <span class="shrink-0 w-5 text-center text-[10px] font-bold tabular-nums"
                    :class="hasStaleConditions(item, idx)
                      ? 'text-amber-500 dark:text-amber-400'
                      : 'text-gray-300 dark:text-gray-600'">
                {{ idx + 1 }}
              </span>

              <!-- Type chip -->
              <span v-if="questionById(item.question_id)"
                    class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide leading-none"
                    :class="typeColor(questionById(item.question_id).question_type)">
                <span v-html="typeIconSm(questionById(item.question_id).question_type)"></span>
                {{ typeLabel(questionById(item.question_id).question_type) }}
              </span>

              <!-- Title -->
              <span class="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                {{ questionById(item.question_id)?.title ?? 'Unknown question' }}
              </span>

              <!-- Required / Optional pill toggle -->
              <button type="button"
                      @click.stop="item.required = !item.required"
                      class="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition-colors focus:outline-none"
                      :class="item.required
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/60'
                        : 'text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-600 dark:hover:text-gray-400'">
                <svg v-if="item.required" class="w-2.5 h-2.5 fill-current shrink-0" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                {{ item.required ? 'Required' : 'Optional' }}
              </button>

              <!-- Trailing actions — always a single shrink-0 unit so they never break to a new row -->
              <div class="shrink-0 flex items-center gap-0.5">
                <!-- Logic button — only when conditional logic is enabled -->
                <button v-if="conditionalLogicEnabled"
                        type="button"
                        @click.stop="item._logicOpen = !item._logicOpen"
                        class="relative w-7 h-7 flex items-center justify-center rounded-lg transition-colors focus:outline-none"
                        :class="item._logicOpen
                          ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                          : hasConditions(item)
                            ? 'text-violet-500 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20'
                            : 'text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'"
                        title="Conditional logic">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 12m0 0 4.5-9M3 12h13.5m0 0 4.5-9m-4.5 9 4.5 9"/>
                  </svg>
                  <span v-if="hasConditions(item) && !item._logicOpen"
                        class="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-violet-500 dark:bg-violet-400 border-2 border-white dark:border-[#1C2128]" />
                </button>

                <!-- Remove -->
                <button type="button"
                        @click.stop="removeItem(idx)"
                        class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

            </div>

            <!-- ── Condition summary strip (only when conditional logic enabled) ── -->
            <div v-if="conditionalLogicEnabled && hasConditions(item) && !item._logicOpen"
                 class="flex items-center gap-1.5 px-4 pb-2.5 -mt-1">
              <svg v-if="hasStaleConditions(item, idx)"
                   class="w-3 h-3 text-amber-500 dark:text-amber-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
              </svg>
              <svg v-else class="w-3 h-3 text-violet-400 dark:text-violet-500 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 12m0 0 4.5-9M3 12h13.5m0 0 4.5-9m-4.5 9 4.5 9"/>
              </svg>
              <span class="text-[11px] truncate"
                    :class="hasStaleConditions(item, idx)
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-violet-600 dark:text-violet-400'">
                {{ conditionSummary(item, idx) }}
              </span>
              <span v-if="hasStaleConditions(item, idx)"
                    class="shrink-0 text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
                — reorder changed sources, review logic
              </span>
            </div>

            <!-- ── Logic builder (expands below the card row — only when enabled) ── -->
            <Transition name="logic-expand">
              <div v-if="conditionalLogicEnabled && item._logicOpen"
                   class="border-t border-gray-100 dark:border-gray-700/60 px-4 pt-4 pb-4 bg-gray-50/60 dark:bg-gray-800/20 select-text"
                   @click.stop @dragstart.stop>

                <!-- Header -->
                <div class="flex items-center justify-between mb-3">
                  <div>
                    <p class="text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      When to show this question
                    </p>
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                      Question #{{ idx + 1 }} can only reference questions that appear before it.
                    </p>
                  </div>
                  <div class="flex items-center gap-2 shrink-0 ml-2">
                    <button v-if="item.conditions.rules.length > 0"
                            type="button"
                            @click="clearConditions(item)"
                            class="text-[11px] text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                      Clear all
                    </button>
                    <a href="/help-center/bookings/conditional-logic"
                       target="_blank"
                       rel="noopener"
                       class="w-5 h-5 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-violet-400 dark:hover:border-violet-600 hover:text-violet-600 dark:hover:text-violet-400 transition-all"
                       title="Conditional logic docs">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <!-- Stale conditions banner -->
                <div v-if="hasStaleConditions(item, idx)"
                     class="flex items-start gap-2 px-3 py-2.5 mb-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
                  <svg class="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
                  </svg>
                  <div class="min-w-0">
                    <p class="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Order changed — conditions need review</p>
                    <p class="text-[10px] text-amber-600/80 dark:text-amber-400/80 mt-0.5 leading-relaxed">
                      One or more source questions are now positioned after this question. Remove or update the highlighted rules below.
                    </p>
                  </div>
                </div>

                <!-- No rules — empty state -->
                <div v-if="item.conditions.rules.length === 0"
                     class="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 px-4 py-5 text-center">
                  <div class="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center mx-auto mb-2">
                    <svg class="w-4 h-4 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 12m0 0 4.5-9M3 12h13.5m0 0 4.5-9m-4.5 9 4.5 9"/>
                    </svg>
                  </div>
                  <p class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">No conditions set</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mb-1 leading-relaxed max-w-[220px] mx-auto">
                    This question always shows. Add a condition to control when it appears based on a previous answer.
                  </p>
                  <p v-if="availableSources(idx).length === 0" class="text-[10px] text-amber-600 dark:text-amber-400 mb-3">
                    {{ idx === 0 ? 'This is question #1 — there are no questions above it to reference.' : 'No questions above this one have a type that can trigger conditions (dropdown, radio, checkbox, boolean).' }}
                  </p>
                  <p v-else class="text-[10px] text-gray-400 dark:text-gray-500 mb-3">
                    {{ availableSources(idx).length }} question{{ availableSources(idx).length === 1 ? '' : 's' }} above this one can be used as a source.
                  </p>
                  <button type="button" @click="addRule(item, idx)"
                          :disabled="availableSources(idx).length === 0"
                          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          :class="availableSources(idx).length > 0
                            ? 'bg-violet-600 hover:bg-violet-700 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-400'">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                    </svg>
                    {{ availableSources(idx).length === 0 ? 'No eligible source questions' : 'Add a condition' }}
                  </button>
                </div>

                <!-- Rules list -->
                <div v-else class="space-y-3">

                  <!-- Each rule row -->
                  <div v-for="(rule, rIdx) in item.conditions.rules" :key="rule.id"
                       class="rounded-xl border p-3 space-y-2.5 transition-colors"
                       :class="isRuleStale(rule, idx)
                         ? 'border-amber-300 dark:border-amber-600 bg-amber-50/60 dark:bg-amber-900/10'
                         : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]'">

                    <!-- Rule header -->
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-1.5">
                        <span class="text-[10px] font-bold uppercase tracking-wider"
                              :class="isRuleStale(rule, idx) ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400'">
                          {{ rIdx === 0 ? 'If' : (item.conditions.match === 'all' ? 'And' : 'Or') }}
                        </span>
                        <span v-if="isRuleStale(rule, idx)"
                              class="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                          Source moved — remove or update
                        </span>
                      </div>
                      <button type="button" @click="removeRule(item, rIdx)"
                              class="w-5 h-5 flex items-center justify-center rounded transition-colors"
                              :class="isRuleStale(rule, idx)
                                ? 'text-amber-400 hover:text-red-500 dark:text-amber-400 dark:hover:text-red-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-red-400 dark:hover:text-red-400'">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>

                    <!-- Source question picker -->
                    <div>
                      <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                        Based on question
                        <span class="font-normal normal-case ml-1 text-gray-400">(only questions above #{{ idx + 1 }})</span>
                      </label>
                      <select v-model="rule.source_event_question_id"
                              @change="onSourceChange(rule)"
                              class="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                              :class="isRuleStale(rule, idx)
                                ? 'border-amber-300 dark:border-amber-600'
                                : 'border-gray-200 dark:border-gray-700'">
                        <option value="" disabled>Select a question…</option>
                        <option v-for="src in availableSources(idx)" :key="src.item.id" :value="src.item.id">
                          #{{ items.indexOf(src.item) + 1 }} — {{ src.question.title }}
                        </option>
                      </select>
                      <p v-if="isRuleStale(rule, idx)" class="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                        The selected source is no longer above this question. Select a new source or remove this rule.
                      </p>
                    </div>

                    <!-- Operator + Value row -->
                    <div class="grid grid-cols-2 gap-2" v-if="rule.source_event_question_id">
                      <!-- Operator -->
                      <div>
                        <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Condition</label>
                        <select v-model="rule.operator"
                                @change="onOperatorChange(rule)"
                                class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                          <option v-for="op in operatorsForSource(rule.source_event_question_id)" :key="op.value" :value="op.value">
                            {{ op.label }}
                          </option>
                        </select>
                      </div>

                      <!-- Value input — smart per source type and date_mode -->
                      <div v-if="valueInputType(sourceType(rule.source_event_question_id), rule.operator, sourceDetails(rule.source_event_question_id)) !== 'none'">
                        <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Value</label>

                        <!-- options — drop_down / radio_button / check_box -->
                        <select v-if="valueInputType(sourceType(rule.source_event_question_id), rule.operator, sourceDetails(rule.source_event_question_id)) === 'options'"
                                v-model="rule.value"
                                class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                          <option value="" disabled>Select…</option>
                          <option v-for="opt in sourceOptions(rule.source_event_question_id)" :key="opt" :value="opt">{{ opt }}</option>
                        </select>

                        <!-- boolean — Yes / No -->
                        <select v-else-if="valueInputType(sourceType(rule.source_event_question_id), rule.operator, sourceDetails(rule.source_event_question_id)) === 'boolean'"
                                v-model="rule.value"
                                class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                          <option value="" disabled>Select…</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>

                        <!-- number / rating — numeric input -->
                        <input v-else-if="valueInputType(sourceType(rule.source_event_question_id), rule.operator, sourceDetails(rule.source_event_question_id)) === 'number'"
                               v-model="rule.value"
                               type="number"
                               placeholder="e.g. 10"
                               class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />

                        <!-- date — date picker (date mode and date_range mode) -->
                        <input v-else-if="valueInputType(sourceType(rule.source_event_question_id), rule.operator, sourceDetails(rule.source_event_question_id)) === 'date'"
                               v-model="rule.value"
                               type="date"
                               class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />

                        <!-- time — time picker (time mode) -->
                        <input v-else-if="valueInputType(sourceType(rule.source_event_question_id), rule.operator, sourceDetails(rule.source_event_question_id)) === 'time'"
                               v-model="rule.value"
                               type="time"
                               class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />

                        <!-- datetime — datetime-local picker (datetime mode) -->
                        <input v-else-if="valueInputType(sourceType(rule.source_event_question_id), rule.operator, sourceDetails(rule.source_event_question_id)) === 'datetime'"
                               v-model="rule.value"
                               type="datetime-local"
                               class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />

                        <!-- text — plain input -->
                        <input v-else
                               v-model="rule.value"
                               type="text"
                               placeholder="Enter value…"
                               class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                      </div>
                      <div v-else class="flex items-end pb-1">
                        <p class="text-[11px] text-gray-400 dark:text-gray-500 italic">No value needed</p>
                      </div>
                    </div>

                  </div>

                  <!-- Add another rule -->
                  <button type="button" @click="addRule(item, idx)"
                          :disabled="availableSources(idx).length === 0"
                          class="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium border border-dashed transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          :class="availableSources(idx).length > 0
                            ? 'border-violet-200 dark:border-violet-800/60 text-violet-600 dark:text-violet-400 hover:bg-violet-50/50 dark:hover:bg-violet-900/10'
                            : 'border-gray-200 dark:border-gray-700 text-gray-400'">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                    </svg>
                    Add another condition
                  </button>

                </div>

                <!-- Action + extra inputs — only shown when rules exist -->
                <template v-if="item.conditions.rules.length > 0">

                  <!-- AND / OR match toggle — only meaningful with 2+ rules -->
                  <div v-if="item.conditions.rules.length >= 2" class="flex items-center gap-2 mt-3">
                    <span class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0">Match</span>
                    <div class="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs font-semibold">
                      <button type="button"
                              @click="item.conditions.match = 'any'"
                              class="px-3 py-1.5 transition-colors"
                              :class="item.conditions.match !== 'all'
                                ? 'bg-violet-600 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'">
                        Any (OR)
                      </button>
                      <button type="button"
                              @click="item.conditions.match = 'all'"
                              class="px-3 py-1.5 transition-colors border-l border-gray-200 dark:border-gray-700"
                              :class="item.conditions.match === 'all'
                                ? 'bg-violet-600 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'">
                        All (AND)
                      </button>
                    </div>
                    <p class="text-[10px] text-gray-400 dark:text-gray-500">
                      {{ item.conditions.match === 'all' ? 'All conditions must be true' : 'At least one condition must be true' }}
                    </p>
                  </div>

                  <!-- Divider -->
                  <div class="border-t border-gray-100 dark:border-gray-700/60 my-3" />

                  <!-- Action selector -->
                  <div>
                    <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">What happens when the condition is met</label>
                    <div class="grid grid-cols-2 gap-2">
                      <label v-for="action in ACTIONS" :key="action.value"
                             class="relative flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all select-none"
                             :class="item.conditions.action === action.value
                               ? action.activeClass
                               : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
                        <input type="radio" :value="action.value" v-model="item.conditions.action" class="sr-only" />
                        <span v-html="action.icon" :class="item.conditions.action === action.value ? action.iconActiveClass : 'text-gray-400 dark:text-gray-600'" />
                        <div>
                          <p class="text-xs font-semibold leading-tight"
                             :class="item.conditions.action === action.value ? action.textClass : 'text-gray-600 dark:text-gray-400'">
                            {{ action.label }}
                          </p>
                          <p class="text-[10px] leading-tight mt-0.5"
                             :class="item.conditions.action === action.value ? action.subTextClass : 'text-gray-400 dark:text-gray-500'">
                            {{ action.sub }}
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <!-- Block message input -->
                  <Transition name="logic-expand">
                    <div v-if="item.conditions.action === 'block'" class="mt-3">
                      <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                        Message shown to booker <span class="font-normal normal-case">(optional)</span>
                      </label>
                      <textarea v-model="item.conditions.block_message"
                                rows="2"
                                placeholder="Based on your answers, this booking type may not be the right fit. Please contact us directly."
                                class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none" />
                    </div>
                  </Transition>

                  <!-- Redirect URL input -->
                  <Transition name="logic-expand">
                    <div v-if="item.conditions.action === 'redirect'" class="mt-3">
                      <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                        Redirect URL <span class="text-red-400">*</span>
                      </label>
                      <input v-model="item.conditions.redirect_url"
                             type="url"
                             placeholder="https://example.com/alternative-page"
                             class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                      <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Booker will be redirected here 1.5 seconds after the condition is met.</p>
                    </div>
                  </Transition>

                </template>

              </div>
            </Transition>

          </div>

          <!-- Add another question -->
          <button v-if="canAddMore" type="button" @click="openPanel"
                  class="mt-1 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 border border-dashed border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            Add another question
          </button>
        </div>

        <!-- ── No questions added yet — full CTA ─────────── -->
        <button v-else type="button" @click="openPanel"
                class="w-full flex items-center justify-center gap-2 py-4 rounded-xl border border-dashed border-blue-200 dark:border-blue-800/60 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          Add your first question
        </button>
        </div><!-- end questions tab wrapper -->

        </template><!-- end questions-exist block -->

      </template>
    </template>

    <!-- ─── Question picker panel ──────────────────────────── -->
    <Teleport to="body">
      <Transition name="qpanel-fade">
        <div v-if="panelOpen"
             class="fixed inset-0 z-[200] bg-black/30 dark:bg-black/50"
             @click="closePanel" />
      </Transition>

      <Transition name="qpanel-drawer">
        <div v-if="panelOpen"
             class="qpanel-sheet fixed z-[201] bottom-0 left-0 right-0 sm:top-0 sm:left-auto sm:w-[520px] bg-white dark:bg-[#1C2128] shadow-2xl flex flex-col rounded-t-2xl sm:rounded-none"
             @click.stop>

          <div class="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700/60 shrink-0">
            <div>
              <h3 class="text-sm font-bold text-gray-900 dark:text-white">Add questions</h3>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Tap a question to add it to your form</p>
            </div>
            <button type="button" @click="closePanel"
                    class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 shrink-0">
            <div class="relative">
              <svg class="absolute left-3 top-2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
              <input ref="searchInput" v-model="searchQuery" type="text" placeholder="Search questions..."
                     class="w-full pl-9 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 rounded-lg border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     @click.stop />
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <div v-if="filteredAvailable.length === 0 && filteredAdded.length === 0"
                 class="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
              No questions match
            </div>
            <template v-else>
              <template v-if="filteredAvailable.length > 0">
                <p v-if="filteredAdded.length > 0"
                   class="px-5 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Available</p>
                <button v-for="q in filteredAvailable" :key="q.id" type="button" @click="addQuestion(q.id)"
                        class="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <span class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide leading-none"
                        :class="typeColor(q.question_type)">
                    <span v-html="typeIconSm(q.question_type)"></span>
                    {{ typeLabel(q.question_type) }}
                  </span>
                  <span class="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{{ q.title }}</span>
                  <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                  </svg>
                </button>
              </template>
              <template v-if="filteredAdded.length > 0">
                <p class="px-5 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Already added</p>
                <div v-for="q in filteredAdded" :key="q.id" class="flex items-center gap-3 px-5 py-3 opacity-40">
                  <span class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide leading-none"
                        :class="typeColor(q.question_type)">
                    <span v-html="typeIconSm(q.question_type)"></span>
                    {{ typeLabel(q.question_type) }}
                  </span>
                  <span class="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{{ q.title }}</span>
                  <svg class="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                  </svg>
                </div>
              </template>
            </template>
          </div>

          <div class="px-4 py-4 border-t border-gray-100 dark:border-gray-700/60 shrink-0">
            <button type="button" @click="closePanel"
                    class="w-full py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors">
              Done
            </button>
          </div>

        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useQuestionTypes } from 'composables/useQuestionTypes.js'
import { useConditions }    from 'composables/useConditions.js'
import { useQuestionsStore } from 'stores/questions.js'
import LogicCanvas from './LogicCanvas.vue'

const props = defineProps({
  modelValue:   { type: Array,  default: () => [] },
  initialItems: { type: Array,  default: () => [] },
  eventName:    { type: String, default: ''       },
})
const emit = defineEmits(['update:modelValue'])

const { typeLabel, typeColor, typeIconSm } = useQuestionTypes()
const { operatorsForType, canBeSource, valueInputType, OPERATOR_LABELS } = useConditions()

// ── Store ──────────────────────────────────────────────────
const questionsStore  = useQuestionsStore()
const activeQuestions = computed(() => questionsStore.questions.filter(q => q.state === 'active'))
const loading         = computed(() => questionsStore.loading)

// ── Tab state ──────────────────────────────────────────────
const activeTab               = ref('questions')
const conditionalLogicEnabled = ref(false)
const showDisableConfirm      = ref(false)

// ── Item state ─────────────────────────────────────────────
const items        = ref([])
const deletedItems = ref([])
const panelOpen    = ref(false)
const searchQuery  = ref('')
const searchInput  = ref(null)
const dragIdx      = ref(null)
const dragOverIdx  = ref(null)
let   _keyCounter  = 0
const uniqueKey    = () => ++_keyCounter

function blankConditions() {
  return { rules: [], match: 'any', action: 'show', block_message: '', redirect_url: '' }
}

// ── Hydrate from existing event (edit mode) ────────────────
function hydrateFromInitial() {
  if (!props.initialItems?.length) return
  items.value = props.initialItems.map(eq => ({
    _key:         uniqueKey(),
    _logicOpen:   false,
    id:           eq.id,
    question_id:  eq.question?.id ?? eq.question_id ?? null,
    required:     eq.required ?? false,
    order_number: eq.order_number ?? 1,
    conditions:   eq.conditions && Object.keys(eq.conditions).length > 0
                    ? { rules: [], match: 'any', action: 'show', block_message: '', redirect_url: '', ...eq.conditions }
                    : blankConditions(),
  }))
}

onMounted(async () => {
  await questionsStore.fetchIfStale()
  hydrateFromInitial()
  // Auto-enable if the event already has conditions configured
  if (items.value.some(it => hasConditions(it))) {
    conditionalLogicEnabled.value = true
  }
})

// ── Emit payload whenever items / deletedItems change ──────
watch([items, deletedItems], () => {
  const payload = [
    ...items.value.map((item, idx) => ({
      id:           item.id || undefined,
      question_id:  item.question_id,
      required:     item.required,
      order_number: idx + 1,
      conditions:   item.conditions,
      _destroy:     false,
    })),
    ...deletedItems.value,
  ]
  emit('update:modelValue', payload)
}, { deep: true })

// ── Computed ───────────────────────────────────────────────
const addedIds  = computed(() => new Set(items.value.map(i => i.question_id).filter(Boolean)))
const canAddMore = computed(() => items.value.length < activeQuestions.value.length)

const totalConditionsCount = computed(() =>
  items.value.reduce((sum, it) => sum + (it.conditions?.rules?.length ?? 0), 0)
)

// ── Conditional logic toggle ───────────────────────────────
function toggleConditionalLogic() {
  if (conditionalLogicEnabled.value) {
    // If any conditions are configured, ask for confirmation first
    if (items.value.some(it => hasConditions(it))) {
      showDisableConfirm.value = true
    } else {
      disableConditionalLogic()
    }
  } else {
    conditionalLogicEnabled.value = true
  }
}

function disableConditionalLogic() {
  items.value.forEach(it => {
    it.conditions = blankConditions()
    it._logicOpen = false
  })
  activeTab.value = 'questions'
  conditionalLogicEnabled.value = false
  showDisableConfirm.value = false
}

// Called by LogicCanvas "Edit" button — switch to Questions tab and open logic panel
function onCanvasEditRequest(item) {
  activeTab.value = 'questions'
  const target = items.value.find(it => it._key === item._key)
  if (target) {
    // Close all others, open this one
    items.value.forEach(it => { it._logicOpen = false })
    target._logicOpen = true
  }
}

function questionById(id) {
  return activeQuestions.value.find(q => q.id === id)
}

function matchesSearch(q) {
  const s = searchQuery.value.toLowerCase().trim()
  return !s || q.title.toLowerCase().includes(s) || q.question_type.toLowerCase().includes(s)
}

const filteredAvailable = computed(() =>
  activeQuestions.value.filter(q => !addedIds.value.has(q.id) && matchesSearch(q))
)
const filteredAdded = computed(() =>
  activeQuestions.value.filter(q => addedIds.value.has(q.id) && matchesSearch(q))
)

// ── Condition helpers ──────────────────────────────────────

function hasConditions(item) {
  return item.conditions?.rules?.length > 0
}

// Items before `idx` that have a saved ID and can be a condition source
function availableSources(idx) {
  return items.value
    .slice(0, idx)
    .filter(it => it.id && canBeSource(questionById(it.question_id)?.question_type))
    .map(it => ({ item: it, question: questionById(it.question_id) }))
    .filter(s => s.question)
}

// Set of valid source IDs for a given position (used for stale detection)
function validSourceIdSet(idx) {
  return new Set(availableSources(idx).map(s => s.item.id))
}

// A rule is stale when its source no longer appears above this question
function isRuleStale(rule, idx) {
  if (!rule.source_event_question_id) return false
  return !validSourceIdSet(idx).has(rule.source_event_question_id)
}

function hasStaleConditions(item, idx) {
  return (item.conditions?.rules ?? []).some(r => isRuleStale(r, idx))
}

// Human-readable one-line summary of the condition for display on the card
const ACTION_VERB = {
  show:     'Shows only if',
  hide:     'Hides when',
  require:  'Required when',
  block:    'Blocks if',
  redirect: 'Redirects if',
}

function conditionSummary(item, idx) {
  const rules  = item.conditions?.rules ?? []
  if (!rules.length) return null
  const action = item.conditions?.action ?? 'show'
  const verb   = ACTION_VERB[action] ?? 'Shows only if'

  if (rules.length === 1) {
    const rule       = rules[0]
    const srcItem    = items.value.find(it => it.id === rule.source_event_question_id)
    const srcTitle   = srcItem ? (questionById(srcItem.question_id)?.title ?? 'unknown') : 'unknown'
    const opLabel    = OPERATOR_LABELS[rule.operator] ?? rule.operator
    const valueStr   = rule.value ? ` "${rule.value}"` : ''
    const stale      = isRuleStale(rule, idx)
    const prefix     = stale ? '⚠ ' : ''
    return `${prefix}${verb} "${srcTitle.slice(0, 28)}" ${opLabel}${valueStr}`
  }

  const stale = rules.some(r => isRuleStale(r, idx))
  return `${stale ? '⚠ ' : ''}${verb} ${rules.length} conditions`
}

function sourceType(sourceEqId) {
  const src = items.value.find(it => it.id === sourceEqId)
  return src ? questionById(src.question_id)?.question_type : null
}

function sourceOptions(sourceEqId) {
  const src = items.value.find(it => it.id === sourceEqId)
  if (!src) return []
  const q = questionById(src.question_id)
  return q?.question_details?.options ?? []
}

function sourceDetails(sourceEqId) {
  const src = items.value.find(it => it.id === sourceEqId)
  return src ? (questionById(src.question_id)?.question_details ?? {}) : {}
}

function operatorsForSource(sourceEqId) {
  const type = sourceType(sourceEqId)
  if (!type) return []
  return operatorsForType(type).map(op => ({ value: op, label: OPERATOR_LABELS[op] ?? op }))
}

function isValueless(operator) {
  return operator === 'is_answered' || operator === 'is_blank'
}

function blankRule(sourceEqId = '') {
  return {
    id:                       crypto.randomUUID(),
    source_event_question_id: sourceEqId,
    operator:                 '',
    value:                    '',
  }
}

function addRule(item, idx) {
  const sources = availableSources(idx)
  const firstSrc = sources[0]?.item.id ?? ''
  const rule = blankRule(firstSrc)
  // Pre-select the first valid operator for the chosen source
  if (firstSrc) {
    const ops = operatorsForSource(firstSrc)
    if (ops.length) rule.operator = ops[0].value
  }
  item.conditions.rules.push(rule)
}

function removeRule(item, rIdx) {
  item.conditions.rules.splice(rIdx, 1)
  if (item.conditions.rules.length === 0) {
    item.conditions.action = 'show'
    item.conditions.block_message = ''
  }
}

function clearConditions(item) {
  item.conditions = blankConditions()
}

function onSourceChange(rule) {
  const ops = operatorsForSource(rule.source_event_question_id)
  rule.operator = ops.length ? ops[0].value : ''
  rule.value    = ''
}

function onOperatorChange(rule) {
  if (isValueless(rule.operator)) rule.value = ''
}

// ── Action config ──────────────────────────────────────────
const ACTIONS = [
  {
    value:         'show',
    label:         'Show question',
    sub:           'Display when conditions are met',
    icon:          `<svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>`,
    activeClass:   'border-blue-500 bg-blue-50/60 dark:bg-blue-900/10',
    iconActiveClass: 'text-blue-600 dark:text-blue-400',
    textClass:     'text-blue-700 dark:text-blue-400',
    subTextClass:  'text-blue-500/70 dark:text-blue-400/60',
  },
  {
    value:         'hide',
    label:         'Hide question',
    sub:           'Conceal when conditions are met',
    icon:          `<svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>`,
    activeClass:   'border-gray-500 bg-gray-50/60 dark:bg-gray-800/40',
    iconActiveClass: 'text-gray-600 dark:text-gray-300',
    textClass:     'text-gray-700 dark:text-gray-300',
    subTextClass:  'text-gray-400 dark:text-gray-500',
  },
  {
    value:         'require',
    label:         'Make required',
    sub:           'Force an answer when conditions are met',
    icon:          `<svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>`,
    activeClass:   'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/10',
    iconActiveClass: 'text-emerald-600 dark:text-emerald-400',
    textClass:     'text-emerald-700 dark:text-emerald-400',
    subTextClass:  'text-emerald-500/70 dark:text-emerald-400/60',
  },
  {
    value:         'block',
    label:         'Block booking',
    sub:           'Prevent scheduling when conditions are met',
    icon:          `<svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/></svg>`,
    activeClass:   'border-red-500 bg-red-50/60 dark:bg-red-900/10',
    iconActiveClass: 'text-red-600 dark:text-red-400',
    textClass:     'text-red-700 dark:text-red-400',
    subTextClass:  'text-red-500/70 dark:text-red-400/60',
  },
  {
    value:         'redirect',
    label:         'Redirect to URL',
    sub:           'Send booker to another page',
    icon:          `<svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>`,
    activeClass:   'border-orange-500 bg-orange-50/60 dark:bg-orange-900/10',
    iconActiveClass: 'text-orange-600 dark:text-orange-400',
    textClass:     'text-orange-700 dark:text-orange-400',
    subTextClass:  'text-orange-500/70 dark:text-orange-400/60',
  },
]

// ── Panel ──────────────────────────────────────────────────
function openPanel() {
  panelOpen.value   = true
  searchQuery.value = ''
  nextTick(() => searchInput.value?.focus())
}
function closePanel() {
  panelOpen.value   = false
  searchQuery.value = ''
}
function onKeydown(e) {
  if (e.key === 'Escape' && panelOpen.value) closePanel()
}
onMounted(()  => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

// ── Item actions ───────────────────────────────────────────
function addQuestion(questionId) {
  items.value.push({
    _key:         uniqueKey(),
    _logicOpen:   false,
    id:           null,
    question_id:  questionId,
    required:     false,
    order_number: items.value.length + 1,
    conditions:   blankConditions(),
  })
}

function removeItem(idx) {
  const item = items.value[idx]
  if (item.id) deletedItems.value.push({ id: item.id, _destroy: true })
  items.value.splice(idx, 1)
}

// ── Drag to reorder ────────────────────────────────────────
function onDragStart(idx) { dragIdx.value = idx }
function onDragOver(idx)  { dragOverIdx.value = idx }
function onDragEnd()      { dragIdx.value = null; dragOverIdx.value = null }
function onDrop(idx) {
  if (dragIdx.value === null || dragIdx.value === idx) {
    dragIdx.value = null; dragOverIdx.value = null; return
  }
  const moved = items.value.splice(dragIdx.value, 1)[0]
  items.value.splice(idx, 0, moved)
  dragIdx.value = null; dragOverIdx.value = null
  // Close all logic panels — sources change when order changes
  items.value.forEach(it => { it._logicOpen = false })
}
</script>

<style scoped>
/* ── Question picker panel transitions ── */
.qpanel-fade-enter-active { transition: opacity 0.2s ease; }
.qpanel-fade-leave-active { transition: opacity 0.15s ease; }
.qpanel-fade-enter-from,
.qpanel-fade-leave-to     { opacity: 0; }

.qpanel-drawer-enter-active { transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1); }
.qpanel-drawer-leave-active { transition: transform 0.22s ease-in; }
.qpanel-drawer-enter-from,
.qpanel-drawer-leave-to     { transform: translateY(100%); }

@media (min-width: 640px) {
  .qpanel-drawer-enter-from,
  .qpanel-drawer-leave-to   { transform: translateX(100%); }
}

.qpanel-sheet { height: 85dvh; }
@media (min-width: 640px) {
  .qpanel-sheet { height: auto; }
}

/* ── Logic builder expand/collapse ── */
.logic-expand-enter-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  max-height: 600px;
  overflow: hidden;
}
.logic-expand-leave-active {
  transition: max-height 0.18s ease, opacity 0.15s ease;
  max-height: 600px;
  overflow: hidden;
}
.logic-expand-enter-from,
.logic-expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
